import {
  setState,
  setEvents as setEventsCommon,
  clearState,
} from '@/store/common/mutations';
import { getEventsAction } from '@/store/common/actions';
import {
  groupsService,
  modulesService,
  competencesService,
  professionsService,
} from '@/services';
import mapNames from '@/tools/mapnames';
import { pluralizeWeeks } from '@/tools/pluralize';

let store;

export default function (providers) {
  const { $api, $captureError } = providers;
  store = store || {
    namespaced: true,

    state: () => ({
      seo_title: '',
      seo_description: '',
      seo_keywords: '',
      name: '',
      section_id: '',
      sub_section_id: '',
      dataIsLoaded: false,
      recommended: [],
      question_list: [],
      answer_list: [],
      teachers_list: [],
      modules: [],
      competensesList: [],
      professionsList: [],
      error: false,
      disabledRequest: false,
    }),

    mutations: {
      setState,
      clearState,
      setEvents(state, payload) {
        return setEventsCommon(state, payload, 'recommended');
      },
      resetReviewsCount(state, payload) {
        state.reviews_count = payload;
      },
      setQuestionList(state, payload) {
        state.question_list = payload;
      },
      setAnswerList(state, payload) {
        state.answer_list = payload;
      },
      setTeachersList(state, payload) {
        state.teachers_list = payload;
      },
      setModules(state, payload) {
        state.modules = payload;
      },
      setCompetensesList(state, payload) {
        state.competensesList = payload;
      },
      setProfessionList(state, payload) {
        state.professionsList = payload;
      },
      toggleRequestButton(state, payload) {
        state.disabledRequest = payload;
      },
    },

    actions: {
      init({ rootGetters, dispatch, commit }, params) {
        commit('clearState');
        commit('setState', { dataIsLoaded: false });
        const list = [
          dispatch('getEvent', params),
          dispatch('getEvents', params),
          dispatch('getModules', params),
          dispatch('getCompetensesList', params),
          dispatch('getProfessionList', params),
          //dispatch('getSchedule', params),
        ];
        if (rootGetters.hasApiModuleGuru)
          list.push(dispatch('getTeachers', params));
        return Promise.all(list).then(() =>
          commit('setState', { dataIsLoaded: true })
        );
      },

      loadEvent({}, { id }) {
        return $api.get(`event/get/${id}`).then(({ data }) => data);
      },

      async getEvent({ commit, dispatch }, params) {
        const getEduPartnersByEventId = async (id) => {
          try {
            const {
              data: { data: eduPartners },
            } = await $api.get(`/event/edu-partners/get/${id}`);
            return eduPartners;
          } catch (e) {
            return [];
          }
        };
        const eduPartners = await getEduPartnersByEventId(params.id);
        return dispatch('loadEvent', params)
          .then(({ error_code, errors, data }) => {
            const error = error_code || errors;
            commit('setState', {
              ...data,
              eduPartners: eduPartners,
              edu_mode: data.edu_mode_name,
              edu_cats: mapNames(data.edu_cats_names),
              labor_program: data.labor_program
                ? pluralizeWeeks(data.labor_program)
                : '',
              video:
                typeof data.video === 'string' && data.video.length
                  ? data.video.split('\n').filter((item) => item.length > 0)
                  : [],
              error,
            });
          })
          .catch((err) => {
            if (err.response.status !== 404) $captureError(err);
            commit('setState', { error: true });
          });
      },

      getEvents({ commit }, { id }) {
        const config = { id, url: `event/recommended/get/${id}` };
        return getEventsAction(commit, config, $api);
      },

      async getModules({ commit }, { id }) {
        try {
          const result = await modulesService($api).getModulesByEventCard({
            event: id,
          });
          commit('setModules', Array.isArray(result.data) ? result.data : []);
          return result;
        } catch (err) {
          if (err.response.status !== 404) {
            $captureError(err);
          }
          commit('setState', {
            error: true,
          });
        }
      },

      async getCompetensesList({ commit }, { id }) {
        try {
          const { data } = await competencesService($api).getCompetences({
            event_id: id,
          });
          commit('setCompetensesList', Array.isArray(data) ? data : []);
          return data;
        } catch (err) {
          if (err.response.status !== 404) {
            $captureError(err);
          }
          commit('setState', {
            error: true,
          });
        }
      },

      async getProfessionList({ commit }, { id }) {
        try {
          const { data } = await professionsService($api).getProfessions({
            event_id: id,
          });
          commit('setProfessionList', Array.isArray(data) ? data : []);
          return data;
        } catch (err) {
          if (err.response.status !== 404) {
            $captureError(err);
          }
          commit('setState', {
            error: true,
          });
        }
      },

      getTeachers({ commit }, params) {
        return $api
          .get(`teachers/get/${params.id}`)
          .then(({ data: { data } }) => {
            const count = 8; // Количество заглушек для фото
            const teachers = data.map((teacher) => {
              return {
                ...teacher,
                age: pluralizeAge(teacher.age),
                programsLink: `/directivities?teacher_id=${teacher.id}`,
                image: Math.floor(Math.random() * count) + 1,
              };
            });
            commit('setTeachersList', teachers);
          })
          .catch((err) => {
            if (err.response.status !== 404) {
              $captureError(err);
            }
            commit('setState', {
              error: true,
            });
          });
      },

      getQuestionList({ commit }, id) {
        if (!id) return;
        return $api
          .get('noko/question/list', {
            params: { form_id: id },
          })
          .then(({ data: { data } }) => {
            commit(
              'setQuestionList',
              Array.isArray(data)
                ? data
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map(({ id, text, type, form_id }) => ({
                      text,
                      type,
                      id,
                      form_id,
                    }))
                : []
            );
          })
          .catch((err) => {
            if (err.response.status !== 404) {
              $captureError(err);
            }
            commit('setState', {
              error: true,
            });
          });
      },

      getAnswerList({ commit }, { id, target_id }) {
        if (!id || !target_id) return;
        return $api
          .get('noko/result/list', {
            params: { form_id: id, target_id },
          })
          .then(({ data: { data } }) => {
            commit(
              'setAnswerList',
              Array.isArray(data)
                ? data.map(({ answers }) => ({ answers }))
                : []
            );
          })
          .catch((err) => {
            if (err.response.status !== 404) {
              $captureError(err);
            }
            commit('setState', {
              error: true,
            });
          });
      },

      setProgramRequest({ commit }, { id }) {
        return $api
          .post('/order/create', { data: { event_id: id } })
          .then(({ data: { data } }) => {
            commit('toggleRequestButton', true);
          });
      },
    },
  };
  return store;
}



// WEBPACK FOOTER //
// ./src/pages/event/card/store.js